import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Search, Plus, X } from 'lucide-react';
import { getUserTypeDisplayName, getUserTypeBadgeVariant } from '@/utils/userTypeDisplay';

interface User {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  user_type: 'supabase' | 'internal';
}

interface TeamCoach {
  id: string;
  coach_id: string;
  role?: string;
  user?: User;
}

interface AddCoachFormProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamName: string;
  teamSport?: string;
  onUpdate: () => void;
}

import { getCoachRolesForSport, formatRoleName } from '@/data/sportRoles';

export const AddCoachForm = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  teamSport = 'Basketball',
  onUpdate
}: AddCoachFormProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [coaches, setCoaches] = useState<TeamCoach[]>([]);

  // Load current team coaches
  const loadTeamCoaches = async () => {
    try {
      setLoading(true);
      
      const { data: teamCoaches, error: coachesError } = await supabase
        .from('team_coaches')
        .select('*')
        .eq('team_id', teamId);

      if (!coachesError && teamCoaches) {
        const coachesWithUsers = await Promise.all(
          teamCoaches.map(async (coach) => {
            let userData = null;
            
            // Try Supabase users first
            const { data: profile } = await supabase
              .from('profiles')
              .select('id, username, first_name, last_name, role')
              .eq('id', coach.coach_id)
              .single();
            
            if (profile) {
              userData = { ...profile, user_type: 'supabase' as const };
            } else {
              // Try internal users
              const { data: internalProfile } = await supabase
                .from('internal_user_profiles')
                .select('user_id, username, first_name, last_name, role')
                .eq('user_id', coach.coach_id)
                .single();
              
              if (internalProfile) {
                userData = { 
                  id: internalProfile.user_id, 
                  username: internalProfile.username,
                  first_name: internalProfile.first_name,
                  last_name: internalProfile.last_name,
                  role: internalProfile.role,
                  user_type: 'internal' as const 
                };
              }
            }
            
            return {
              id: coach.coach_id + '-coach',
              coach_id: coach.coach_id,
              role: coach.role,
              user: userData
            };
          })
        );
        setCoaches(coachesWithUsers);
      }
    } catch (error) {
      console.error('Error loading team coaches:', error);
      toast.error('Failed to load team coaches');
    } finally {
      setLoading(false);
    }
  };

  // Search users
  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setAvailableUsers([]);
      return;
    }

    try {
      setLoading(true);
      
      // Search Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Search internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_user_profiles')
        .select('user_id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Combine results
      const combined: User[] = [
        ...(supabaseUsers || []).map(user => ({
          id: user.id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'supabase' as const
        })),
        ...(internalUsers || []).map(user => ({
          id: user.user_id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'internal' as const
        }))
      ];

      // Filter out existing coaches
      const existingCoachIds = coaches.map(c => c.coach_id);
      const filtered = combined.filter(user => !existingCoachIds.includes(user.id));

      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  // Add coach
  const addCoach = async (userId: string, userType: 'supabase' | 'internal') => {
    try {
      // Check if coach is already on the team by checking database directly
      const { data: existingCoach } = await supabase
        .from('team_coaches')
        .select('coach_id')
        .eq('team_id', teamId)
        .eq('coach_id', userId)
        .single();
      
      if (existingCoach) {
        toast.error('This coach is already on the team');
        return;
      }

      const { error } = await supabase
        .from('team_coaches')
        .insert({
          team_id: teamId,
          coach_id: userId,
          role: selectedRole
        });
      
      if (error) {
        console.error('Database error:', error);
        if (error.code === '23505') {
          toast.error('This coach is already on the team');
        } else {
          toast.error(`Failed to add coach: ${error.message}`);
        }
        return;
      }

      toast.success('Coach added successfully');
      loadTeamCoaches();
      onUpdate();
      setSearchTerm('');
      setSelectedRole('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding coach:', error);
      toast.error('Failed to add coach');
    }
  };

  // Remove coach
  const removeCoach = async (coachId: string) => {
    try {
      const { error } = await supabase
        .from('team_coaches')
        .delete()
        .eq('team_id', teamId)
        .eq('coach_id', coachId);
      
      if (error) {
        console.error('Database error:', error);
        toast.error(`Failed to remove coach: ${error.message}`);
        return;
      }

      toast.success('Coach removed successfully');
      loadTeamCoaches();
      onUpdate();
    } catch (error) {
      console.error('Error removing coach:', error);
      toast.error('Failed to remove coach');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTeamCoaches();
    }
  }, [isOpen, teamId]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const getUserDisplayName = (user?: User) => {
    if (!user) return 'Unknown User';
    return user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
  };


  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            Manage Coaches - {teamName} {teamSport && `(${teamSport})`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add Coach Section */}
          <Card>
            <CardHeader>
              <CardTitle>Add New Coach</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="coach-search">Search Coaches</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="coach-search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by username or name..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="coach-role">Coaching Role</Label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select coaching role" />
                    </SelectTrigger>
                    <SelectContent>
                      {getCoachRolesForSport(teamSport).map((role) => (
                        <SelectItem key={role} value={role}>
                          {formatRoleName(role)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Search Results */}
              {searchTerm && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                   {availableUsers.map((user) => {
                     const isAlreadyCoach = coaches.some(c => c.coach_id === user.id);
                     return (
                      <div key={user.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                        <div className="flex items-center gap-3">
                          <div>
                            <span className="font-medium">{getUserDisplayName(user)}</span>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">
                                {user.role}
                              </Badge>
                              <Badge variant={getUserTypeBadgeVariant(user.user_type)} className="text-xs">
                                {getUserTypeDisplayName(user.user_type)}
                              </Badge>
                            </div>
                            {isAlreadyCoach && (
                              <span className="text-xs text-yellow-600 ml-2">(Already on team)</span>
                            )}
                          </div>
                        </div>
                        <Button
                          onClick={() => addCoach(user.id, user.user_type)}
                          size="sm"
                          className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
                          disabled={isAlreadyCoach || !selectedRole}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {isAlreadyCoach ? 'Added' : 'Add'}
                        </Button>
                      </div>
                    );
                  })}
                  {availableUsers.length === 0 && searchTerm && (
                    <p className="text-muted-foreground text-center py-4">No users found</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Coaches Section */}
          <Card>
            <CardHeader>
              <CardTitle>Current Coaches ({coaches.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {coaches.map((coach) => (
                  <div key={coach.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="font-medium">{getUserDisplayName(coach.user)}</span>
                        {coach.role && (
                          <div className="mt-1">
                            <Badge variant="outline" className="text-xs">
                              {formatRoleName(coach.role)}
                            </Badge>
                            {coach.user && (
                              <Badge variant={getUserTypeBadgeVariant(coach.user.user_type)} className="text-xs ml-2">
                                {getUserTypeDisplayName(coach.user.user_type)}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => removeCoach(coach.coach_id)}
                      size="sm"
                      variant="destructive"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {coaches.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">No coaches added yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};